import { useEffect, useRef, useState, type ReactNode, type ReactPortal } from 'react'
import { createPortal } from 'react-dom'
import { Marker } from 'maplibre-gl'
import { useMap } from './mapContext'

export interface MapMarkerProps {
  lat: number
  lon: number
  /** maplibre Marker anchor; default 'bottom' (pin-tip semantics). */
  anchor?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  /** Degrees clockwise; rotationAlignment stays 'map' when set (arrowheads). */
  rotation?: number
  /**
   * CSS z-index on the marker's own root element. Every `MapMarker` on a map is
   * appended as a sibling into maplibre's marker container, so — unlike a
   * z-index set on something inside `children`, which only wins against its own
   * siblings within this one marker — element-level z-index here is what
   * actually orders one marker's paint above or below ANOTHER marker's. Omit it
   * to fall back to append order (later-mounted paints on top).
   */
  zIndex?: number
  /** maplibre Marker constructor option; also updated live via
   *  `marker.setDraggable()` on a prop change after mount. Default false. */
  draggable?: boolean
  onClick?: () => void
  /** Fired once when a drag gesture begins (marker's native 'dragstart'). */
  onDragStart?: () => void
  /** Fired once when a drag gesture ends (marker's native 'dragend'), reading
   *  the marker's resting position via `marker.getLngLat()` — note maplibre's
   *  `lng` is this callback's `lon`. */
  onDragEnd?: (pos: { lat: number; lon: number }) => void
  children: ReactNode
}

/** A DOM-marker portal: `children` render into a div that maplibre-gl
 *  positions and keeps pinned to [lon, lat], so ordinary React content (icons,
 *  badges, interactive widgets) can ride a marker instead of being drawn as a
 *  raster/vector map layer. */
export function MapMarker({
  lat,
  lon,
  anchor = 'bottom',
  rotation,
  zIndex,
  draggable,
  onClick,
  onDragStart,
  onDragEnd,
  children,
}: MapMarkerProps): ReactPortal | null {
  const map = useMap()
  // Created once and portaled into for the component's whole lifetime;
  // maplibre positions this element, React only ever fills its content.
  const [element] = useState(() => document.createElement('div'))
  const markerRef = useRef<Marker | null>(null)

  // Mount-time options, read through a ref so the creation effect can key
  // only on map/element — an anchor/rotation/draggable change repositions or
  // re-flags the existing marker via the dependent effects below rather than
  // tearing it down.
  const initialRef = useRef({ anchor, rotation, draggable })
  initialRef.current = { anchor, rotation, draggable }

  // Latest callbacks via refs so re-renders don't re-bind marker listeners —
  // the binding effect below keys on [map, element], same as marker creation.
  const onDragStartRef = useRef(onDragStart)
  onDragStartRef.current = onDragStart
  const onDragEndRef = useRef(onDragEnd)
  onDragEndRef.current = onDragEnd

  useEffect(() => {
    if (!map) return
    const init = initialRef.current
    const marker = new Marker({
      element,
      anchor: init.anchor,
      rotation: init.rotation,
      rotationAlignment: init.rotation !== undefined ? 'map' : 'auto',
      draggable: init.draggable,
    })
      .setLngLat([lon, lat])
      .addTo(map)
    markerRef.current = marker

    const handleDragStart = () => onDragStartRef.current?.()
    const handleDragEnd = () => {
      const { lng, lat: draggedLat } = marker.getLngLat()
      onDragEndRef.current?.({ lat: draggedLat, lon: lng })
    }
    marker.on('dragstart', handleDragStart)
    marker.on('dragend', handleDragEnd)

    return () => {
      markerRef.current = null
      marker.off('dragstart', handleDragStart)
      marker.off('dragend', handleDragEnd)
      marker.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, element])

  useEffect(() => {
    markerRef.current?.setLngLat([lon, lat])
  }, [lat, lon])

  // Applied straight to the element's own CSS rather than through the Marker
  // API — maplibre has no z-index option, and the element it positions IS the
  // one every other marker on the map is a DOM sibling of, so this is the whole
  // mechanism.
  useEffect(() => {
    element.style.zIndex = zIndex === undefined ? '' : String(zIndex)
  }, [element, zIndex])

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) return
    if (rotation !== undefined) {
      marker.setRotationAlignment('map')
      marker.setRotation(rotation)
    } else {
      // Reset alignment and angle together — a cleared heading shouldn't
      // leave the icon pinned at its last rotation under 'map' alignment.
      marker.setRotationAlignment('auto')
      marker.setRotation(0)
    }
  }, [rotation])

  useEffect(() => {
    markerRef.current?.setDraggable(draggable ?? false)
  }, [draggable])

  // Latest callback via a ref so re-renders don't re-bind the DOM listener.
  const onClickRef = useRef(onClick)
  onClickRef.current = onClick
  useEffect(() => {
    const handleClick = () => onClickRef.current?.()
    element.addEventListener('click', handleClick)
    return () => element.removeEventListener('click', handleClick)
  }, [element])

  if (!map) return null
  return createPortal(children, element)
}
